<template>
  <div class="edu-cooperation-page">
    <!-- 1. Top Banner Section -->
    <div class="top-banner">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('eduCooperation.banner.title') }}</h1>
      </div>
    </div>

    <!-- 2. Advantages Section -->
    <div class="advantages-section">
      <div class="section-container">
        <h2 class="section-title">{{ $t('eduCooperation.advantages.title') }}</h2>
        <div class="advantages-list">
          <div class="advantage-item"
               v-for="(item, index) in advantages"
               :key="index">
            <div class="item-image-wrapper">
              <img :src="item.image"
                   :alt="item.title"
                   class="item-image" />
            </div>
            <h3 class="item-title">{{ item.title }}</h3>
            <p class="item-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Cooperation Mode Section -->
    <div class="mode-section">
      <div class="section-container">
        <h2 class="section-title">{{ $t('eduCooperation.modes.title') }}</h2>
        <div class="mode-content">
          <div class="mode-text">
            <div class="mode-item"
                 v-for="(mode, index) in modes"
                 :key="index">
              <h3 class="mode-item-title">{{ mode.title }}</h3>
              <p class="mode-item-desc">{{ mode.desc }}</p>
            </div>
          </div>
          <div class="mode-image-wrapper">
            <el-carousel trigger="click"
                         height="509px"
                         :autoplay="true">
              <el-carousel-item v-for="(item, index) in modeCarouselImages"
                                :key="index">
                <img :src="item.url"
                     :alt="item.alt"
                     class="mode-image" />
              </el-carousel-item>
            </el-carousel>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Bottom Banner Section -->
    <div class="bottom-banner">
      <div class="bottom-banner-content">
        <h2 class="bottom-banner-text">
          <span v-for="(line, index) in bottomBannerLines"
                :key="`bottom-${index}`">
            {{ line }}<br v-if="index < bottomBannerLines.length - 1">
          </span>
        </h2>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EduCooperation',
  data() {
    return {
      advantageAssets: [
        { image: require('@/assets/cooperation/adv1.png') },
        { image: require('@/assets/cooperation/adv2.png') },
        { image: require('@/assets/cooperation/adv3.png') }
      ],
      modeCarouselAssets: [
        { url:require('@/assets/cooperation/cooperation1.png')},
        { url: require('@/assets/cooperation/cooperation2.png')},
        { url: require('@/assets/cooperation/cooperation3.png')}
      ]
    }
  },
  computed: {
    eduCooperationCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return messages.eduCooperation || {}
    },
    advantages() {
      const list =
        (this.eduCooperationCopy.advantages && this.eduCooperationCopy.advantages.items) || []
      return list.map((item, index) => {
        const asset = this.advantageAssets[index] || {}
        return {
          title: item.title || '',
          desc: item.desc || '',
          image: asset.image || ''
        }
      })
    },
    modes() {
      const list = (this.eduCooperationCopy.modes && this.eduCooperationCopy.modes.items) || []
      return list.map((item) => ({
        title: item.title || '',
        desc: item.desc || ''
      }))
    },
    modeCarouselImages() {
      const alts =
        (this.eduCooperationCopy.modes && this.eduCooperationCopy.modes.carouselAlts) || []
      return this.modeCarouselAssets.map((item, index) => ({
        url: item.url || '',
        alt: alts[index] || ''
      }))
    },
    bottomBannerLines() {
      return (
        (this.eduCooperationCopy.bottomBanner && this.eduCooperationCopy.bottomBanner.lines) ||
        []
      )
    }
  }
}
</script>

<style scoped>
.edu-cooperation-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* 1. Top Banner */
.top-banner {
  width: 100%;
  height: 546px;
  background-image: url("@/assets/cooperation/banner.png");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* Overlay for text visibility if needed, though user specified color 333 which is dark. 
   If bg is dark image, 333 might not show well. 
   Assuming the provided UI image has a light background or overlay. 
   Let's add a light overlay since text is #333. */


.banner-content {
  position: relative;
  z-index: 1;
  text-align: center;
}

.banner-title {
  font-size: 48px;
  font-weight: bold;
  color: #333;
  margin: 0;
}

/* 2. Advantages Section */
.advantages-section {
  width: 100%;
  height: 836px;
  background-color: #f4f6fa;
  padding-top: 80px; /* Adjust as needed for vertical centering */
  box-sizing: border-box;
}

.section-container {
  width: 1200px;
  margin: 0 auto;
}

.section-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 60px;
}

.advantages-list {
  display: flex;
  justify-content: space-between;
}

.advantage-item {
  width: 342px;
  text-align: center;
}

.item-image-wrapper {
  width: 342px;
  height: 280px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 30px;
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-title {
  font-size: 20px;
  color: #333;
  font-weight: normal; /* "不加粗" */
  margin-bottom: 20px;
}

.item-desc {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  text-align: left; /* Usually descriptions read better left-aligned, but if user wants centered like image... 
                       UI image shows centered or justified? 
                       Text "中泽科教拥有一支..." looks like block text. 
                       Let's use center for title but maybe left/center for desc. 
                       User didn't specify alignment, default block flow is left. 
                       But item is "text-align: center" above. 
                       Let's reset desc to center as it matches the item style often seen. */
  text-align: center;
}

/* 3. Cooperation Mode Section */
.mode-section {
  width: 100%;
  height: 861px;
  background-color: #fff;
  padding-top: 80px;
  box-sizing: border-box;
}

.mode-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mode-text {
  flex: 1;
  padding-right: 50px;
}

.mode-item {
  margin-bottom: 40px;
  display: flex; /* "左文右图" usually means the whole section is text left, image right. 
                   But for the text items themselves: "左边文字的加粗 字号是20px... 描述是..." 
                   It implies "Title: Desc" format or stacked. 
                   "课程引进：合作机构可以..." looks like inline or stacked. 
                   Let's assume stacked for clean layout or inline if short. 
                   The prompt says "左边文字的加粗... 描述是..." describing the styling of the text part. 
                   Let's structure it as Title (Bold) + Desc (Normal). */
}

.mode-item-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  min-width: 100px; /* If inline */
  margin: 0;
  line-height: 1.6;
}

.mode-item-desc {
  font-size: 20px;
  color: #666;
  margin: 0;
  line-height: 1.6;
  flex: 1;
}

.mode-image-wrapper {
  width: 600px;
  height: 509px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
}

.mode-image-wrapper :deep(.el-carousel__container) {
  height: 100%;
}

.mode-image-wrapper :deep(.el-carousel__item) {
  border-radius: 12px;
  overflow: hidden;
}

.mode-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 4. Bottom Banner Section */
.bottom-banner {
  width: 100%;
  height: 600px;
  background-image: url("@/assets/cooperation/aibanner.png"); /* Placeholder for blue tech bg */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  /* "左边有文字" -> align left inside a container? Or just padding? */
  justify-content: center; /* Using container */
}

.bottom-banner-content {
  width: 1200px;
  margin: 0 auto;
}

.bottom-banner-text {
  font-size: 34px;
  color: #333;
  font-weight: normal; /* "不加粗" */
  line-height: 1.5;
  text-align: left;
}
</style>
